import type { Holding } from "@src/components/DefuseSDK/features/account/types/sharedTypes"
import {
  getHoldingFromBalances,
  getHoldingsFromBalances,
  type TokenPrices,
} from "@src/components/DefuseSDK/features/account/utils/holdingsUtils"
import type { BalanceMapping } from "@src/components/DefuseSDK/features/machines/depositedBalanceMachine"
import type {
  BaseTokenInfo,
  TokenInfo,
  TokenValue,
} from "@src/components/DefuseSDK/types/base"
import {
  getDefuseAssetId,
  getTokenAid,
  isBaseToken,
} from "@src/components/DefuseSDK/utils/token"
import {
  adjustDecimals,
  compareAmounts,
  deduplicateTokens,
  getAnyBaseTokenInfo,
  getUnderlyingBaseTokenInfos,
} from "@src/components/DefuseSDK/utils/tokenUtils"
import { isDirectHyperliquidToken } from "./hyperliquid"
import {
  getDestinationUsdValue,
  tokenHasSingleBaseUsd,
} from "./paymentFunding"

export type { TokenPrices } from "@src/components/DefuseSDK/features/account/utils/holdingsUtils"

const MIN_USD_FLOOR = 1

export type PaymentSource = {
  displayToken: TokenInfo
  quoteToken: BaseTokenInfo
  spendableBalance: TokenValue
  usdValue: number
}

export function isSamePaymentFamily(
  paymentToken: TokenInfo,
  tokenIn: TokenInfo
): boolean {
  if (
    isDirectHyperliquidToken(paymentToken) ||
    isDirectHyperliquidToken(tokenIn)
  ) {
    return (
      isBaseToken(paymentToken) &&
      isBaseToken(tokenIn) &&
      getDefuseAssetId(paymentToken) === getDefuseAssetId(tokenIn)
    )
  }

  const paymentAid = getTokenAid(paymentToken)
  const tokenInAid = getTokenAid(tokenIn)
  if (paymentAid != null && tokenInAid != null) {
    return paymentAid === tokenInAid
  }
  return getDefuseAssetId(paymentToken) === getDefuseAssetId(tokenIn)
}

export function resolvePaymentSource(params: {
  paymentToken: TokenInfo
  balances: BalanceMapping
  tokenPrices?: TokenPrices
  siblingCandidates?: BaseTokenInfo[]
}): PaymentSource | null {
  const variants = params.siblingCandidates?.length
    ? deduplicateTokens(params.siblingCandidates)
    : getUnderlyingBaseTokenInfos(params.paymentToken)
  if (variants.length === 0) return null

  let selected: BaseTokenInfo | null = null
  let selectedComparableBalance = 0n
  const maxDecimals = Math.max(...variants.map((v) => v.decimals))

  for (const candidate of variants) {
    const balance = params.balances[candidate.defuseAssetId] ?? 0n
    const comparableBalance = adjustDecimals(
      balance,
      candidate.decimals,
      maxDecimals
    )

    if (selected == null || comparableBalance > selectedComparableBalance) {
      selected = candidate
      selectedComparableBalance = comparableBalance
    }
  }

  if (selected == null) return null

  return {
    displayToken: params.paymentToken,
    quoteToken: selected,
    spendableBalance: {
      amount: params.balances[selected.defuseAssetId] ?? 0n,
      decimals: selected.decimals,
    },
    usdValue:
      getHoldingFromBalances(
        selected,
        params.balances,
        params.tokenPrices ?? {}
      ).usdValue ?? 0,
  }
}

function holdingIsHeld(holding: Holding): boolean {
  return holding.value != null && holding.value.amount > 0n
}

function holdingClearsUsdFloor(holding: Holding): boolean {
  return holding.usdValue == null || holding.usdValue >= MIN_USD_FLOOR
}

function rankedTokensFromHoldings(holdings: Holding[]): TokenInfo[] {
  return holdings
    .filter(holdingIsHeld)
    .filter(holdingClearsUsdFloor)
    .sort((a, b) => {
      if (a.usdValue != null && b.usdValue != null) {
        return b.usdValue - a.usdValue
      }
      if (a.usdValue != null) return -1
      if (b.usdValue != null) return 1
      if (a.value == null || b.value == null) return 0
      return compareAmounts(b.value, a.value)
    })
    .map((holding) => holding.token)
}

export function getRankedHeldTokens(params: {
  tokens: TokenInfo[]
  balances: BalanceMapping
  tokenPrices: TokenPrices
}): TokenInfo[] {
  return rankedTokensFromHoldings(
    getHoldingsFromBalances(params.tokens, params.balances, params.tokenPrices)
  )
}

export function canTokenFundDestinationAmount(params: {
  paymentToken: TokenInfo
  destinationToken: TokenInfo
  requestedAmount: TokenValue
  balances: BalanceMapping
  tokenPrices: TokenPrices
}): boolean {
  const paymentSource = resolvePaymentSource({
    paymentToken: params.paymentToken,
    balances: params.balances,
    tokenPrices: params.tokenPrices,
  })
  if (paymentSource == null) return false

  if (isSamePaymentFamily(params.paymentToken, params.destinationToken)) {
    return (
      compareAmounts(paymentSource.spendableBalance, params.requestedAmount) >=
      0
    )
  }

  const requiredUsd = getDestinationUsdValue({
    destinationToken: params.destinationToken,
    requestedAmount: params.requestedAmount,
    tokenPrices: params.tokenPrices,
  })
  if (requiredUsd == null) return false

  return tokenHasSingleBaseUsd({
    token: params.paymentToken,
    balances: params.balances,
    tokenPrices: params.tokenPrices,
    requiredUsd,
  })
}

export function pickBaseTokenForPayment(
  paymentToken: TokenInfo,
  balances: BalanceMapping
): BaseTokenInfo | null {
  return resolvePaymentSource({ paymentToken, balances })?.quoteToken ?? null
}

/**
 * Resolves a possibly-unified payment token down to a concrete base token to
 * quote against — the funded variant if `balances` is known, else any variant.
 */
export function resolveBaseTokenForPayment(
  paymentToken: TokenInfo,
  balances: BalanceMapping | null | undefined
): BaseTokenInfo {
  if (balances == null) return getAnyBaseTokenInfo(paymentToken)
  return (
    pickBaseTokenForPayment(paymentToken, balances) ??
    getAnyBaseTokenInfo(paymentToken)
  )
}

export function pickDefaultPaymentToken(params: {
  destinationToken: TokenInfo
  heldTokens: TokenInfo[]
  balances: BalanceMapping
  tokenPrices: TokenPrices
}): TokenInfo | null {
  if (
    holdingIsHeld(
      getHoldingFromBalances(
        params.destinationToken,
        params.balances,
        params.tokenPrices
      )
    )
  ) {
    return params.destinationToken
  }

  const ranked = getRankedHeldTokens({
    tokens: params.heldTokens,
    balances: params.balances,
    tokenPrices: params.tokenPrices,
  })
  return ranked[0] ?? null
}

export function hasAlternativePaymentTokens(params: {
  heldTokens: TokenInfo[]
  balances: BalanceMapping
  tokenPrices: TokenPrices
}): boolean {
  const ranked = getRankedHeldTokens({
    tokens: params.heldTokens,
    balances: params.balances,
    tokenPrices: params.tokenPrices,
  })
  return ranked.length > 0
}
